/**
 * Team Inbox — header-menu pure helpers (the "actions collapsed behind the
 * chat header" redesign — AVBP_TEAMINBOX_HEADER_MENU_MOCKUP_2026-06-22).
 *
 * The action bar + triage strip that used to STACK above the messages are
 * collapsed behind a compact, tappable chat header. Tapping it opens an
 * Actions surface (desktop = right slide-over · mobile = bottom sheet) that
 * merges the old Context panel rows WITH the action groups (Sort / Manage).
 *
 * Everything here is PURE (no React, no Firebase) so the Node test runner
 * exercises the contracts directly:
 *   • unreadLensCounts        — per-channel + total UNREAD (top lens, item 4)
 *   • receivedOnLabel         — which inbox a row arrived on (item 5)
 *   • headerIdentity          — name / sub / chips for the compact header
 *   • contextRows / inboxFilterOptions / rowMatchesInbox — the menu + filter model
 *
 * The impure surface (the React slide-over/sheet) lives in
 * components/ThreadHeader.tsx and consumes these.
 */
import type { LiveClient } from '../../../lib/projectStore';
import type { InboxChannel, InboxConversation, NumberRegistry } from './types';
import { CARE_EMAIL } from './emailStore';
import { numberForPhoneId } from './numbers';

/* ───────────────────────── item 4 — unread lens counts ───────────────────────── */

export interface UnreadCounts {
  /** Total unread across BOTH channels. */
  all: number;
  whatsapp: number;
  email: number;
}

/**
 * PURE — per-channel + total UNREAD across the combined inbox rows. Replaces
 * the all-time total-message counts in the top lens (item 4): a meaningless
 * number at scale. WhatsApp unread comes from each conversation's `unread`
 * counter (server `unreadByTeam`); email unread is 0/1 per draft (`read` flag,
 * already folded into `unread` by draftToInboxConversation). Zero → the lens
 * shows no badge (the caller hides an empty count).
 */
export function unreadLensCounts(rows: InboxConversation[]): UnreadCounts {
  let whatsapp = 0;
  let email = 0;
  for (const c of rows) {
    const u = c.unread || 0;
    if (u <= 0) continue;
    if (c.channel === 'email') email += u;
    else whatsapp += u;
  }
  return { all: whatsapp + email, whatsapp, email };
}

/**
 * PURE — per-channel + total CONVERSATION counts (every row, read or unread).
 * The channel segment badge (All / WhatsApp / Email) uses THIS, not
 * `unreadLensCounts`: an unread-only count made the "Email" badge read `0`
 * whenever the inbound emails had already been read — so a populated Email lens
 * looked like "no emails" (P3 Phase-0 email-visibility fix). A total count can
 * never show 0 while the list under it is populated. Unread emphasis, if
 * wanted, is a separate per-row/`unreadLensCounts` signal.
 */
export function channelTotalCounts(rows: InboxConversation[]): UnreadCounts {
  let whatsapp = 0;
  let email = 0;
  for (const c of rows) {
    if (c.channel === 'email') email += 1;
    else whatsapp += 1;
  }
  return { all: whatsapp + email, whatsapp, email };
}

/* ───────────────────────── item 5 — received-on label ───────────────────────── */

export interface ReceivedOn {
  /** Channel glyph + short label, e.g. "📞 7121" / "✉ care@". */
  label: string;
  /** Bare short token for the row chip, e.g. "7121" / "care@". */
  short: string;
  /** Full line for tooltips / the menu Channel row. */
  full: string;
  channel: 'whatsapp' | 'email';
}

/**
 * PURE — resolve which inbox a conversation arrived on (item 5). For WhatsApp,
 * the receiving phone-number id → its friendly registry name (e.g. "Client
 * Care · +91 91793 03585"); for email, the care@ address. Returns null when a
 * WhatsApp row has no resolvable receiving number (legacy pre-multi-sender row)
 * so the caller renders no chip rather than a bare id.
 */
export function receivedOnLabel(
  c: InboxConversation,
  registry: NumberRegistry,
): ReceivedOn | null {
  if (c.channel === 'email') {
    const addr = c.email?.receivedAtAddress || CARE_EMAIL;
    const short = addr.split('@')[0] ? `${addr.split('@')[0]}@` : addr;
    return {
      label: `✉ ${short}`,
      short,
      full: `Email · ${addr}`,
      channel: 'email',
    };
  }
  const num = c.number || numberForPhoneId(registry, c.receivingPhoneNumberId);
  if (!num) return null;
  const last4 = (num.line.match(/(\d{4})\s*$/) || [])[1] || num.label;
  return {
    label: `📞 ${last4}`,
    short: last4,
    full: `WhatsApp · ${num.label} · ${num.line}`,
    channel: 'whatsapp',
  };
}

/* ───────────────────────── item 5 — number-filter options (per-inbox) ───────────────────────── */

export interface InboxFilterOption {
  /** 'all' · a phone-number id · the email sentinel 'email'. */
  id: string;
  label: string;
  /** Sub-line (the line/address). */
  sub: string;
  count: number;
}

/** The sentinel id for the care@ email inbox in the Number dropdown. */
export const EMAIL_INBOX_ID = 'email';

/**
 * PURE — options for the per-inbox "Number" dropdown (item 5). Lists "All
 * inboxes", then each VISIBLE WhatsApp number (friendly name + line + WA count),
 * then the care@ email inbox explicitly (so staff can focus one inbox). Counts
 * are computed over the COMBINED rows so the dropdown is honest about volume.
 */
export function inboxFilterOptions(
  registry: NumberRegistry,
  rows: InboxConversation[],
): InboxFilterOption[] {
  const waRows = rows.filter((r) => r.channel !== 'email');
  const emailRows = rows.filter((r) => r.channel === 'email');
  const waCounts = new Map<string, number>();
  for (const r of waRows) {
    const id = String(r.receivingPhoneNumberId || '').trim();
    if (!id) continue;
    waCounts.set(id, (waCounts.get(id) || 0) + 1);
  }
  const options: InboxFilterOption[] = [
    { id: 'all', label: 'All inboxes', sub: '', count: rows.length },
  ];
  const seen = new Set<string>();
  for (const n of registry.numbers) {
    if (!n.enabled || !n.phoneId) continue;
    seen.add(n.phoneId);
    options.push({
      id: n.phoneId,
      label: n.label,
      sub: n.line,
      count: waCounts.get(n.phoneId) || 0,
    });
  }
  for (const [id, count] of waCounts) {
    if (seen.has(id)) continue;
    options.push({ id, label: id, sub: 'unregistered line', count });
  }
  options.push({
    id: EMAIL_INBOX_ID,
    label: 'Email — care@',
    sub: CARE_EMAIL,
    count: emailRows.length,
  });
  return options;
}

/**
 * PURE — does a row pass the per-inbox Number filter? 'all' → every row; the
 * email sentinel → email rows only; a phone-number id → WA rows whose receiving
 * id matches. One filter predicate governs both channels.
 */
export function rowMatchesInbox(c: InboxConversation, inboxId: string): boolean {
  if (inboxId === 'all') return true;
  if (inboxId === EMAIL_INBOX_ID) return c.channel === 'email';
  if (c.channel === 'email') return false;
  return (c.receivingPhoneNumberId || '') === inboxId;
}

/* ───────────────────────── compact header identity ───────────────────────── */

export interface HeaderChip {
  /** Visual class suffix: 'proj' | 'tri' | 'kn' | 'ch-em' | 'ch-wa'. */
  cls: string;
  text: string;
}

export interface HeaderIdentity {
  avatar: string;
  /** Avatar tint class: '' (WA) | 'kn' | 'tri' | 'em'. */
  avatarClass: string;
  name: string;
  /** Optional secondary identifier shown after the name (C#####). */
  ref: string | null;
  /** Number / email shown under the name. */
  identity: string | null;
  chips: HeaderChip[];
  /** True when the sender is not yet a contact/lead — drives the ⚠ chip + Sort group. */
  unsorted: boolean;
}

/**
 * PURE — the compact header's identity model for a conversation. Resolves a
 * known WhatsApp contact to "Name · C#####"; an email to sender name + address;
 * an unknown sender to the raw number with an ⚠ Unsorted chip. Channel + project
 * chips are appended.
 */
export function headerIdentity(
  c: InboxConversation,
  received: ReceivedOn | null,
  resolved: LiveClient | null,
): HeaderIdentity {
  const chips: HeaderChip[] = [];
  if (c.channel === 'email') {
    const em = c.email;
    const known = !!em?.knownContactId;
    const name = em?.senderName || em?.senderEmail || c.name;
    if (!known) chips.push({ cls: 'tri', text: '⚠ Unsorted' });
    else chips.push({ cls: 'kn', text: '✓ Known contact' });
    if (c.projectRef) chips.push({ cls: 'proj', text: c.projectRef });
    chips.push({ cls: 'ch-em', text: 'Email' });
    return {
      avatar: c.initials,
      avatarClass: 'em',
      name,
      ref: known && em?.knownContactId ? em.knownContactId : null,
      identity: em?.senderEmail || null,
      chips,
      unsorted: !known,
    };
  }
  const unsorted = !resolved;
  if (resolved) chips.push({ cls: 'kn', text: '✓ Known contact' });
  else chips.push({ cls: 'tri', text: '⚠ Unsorted' });
  if (c.projectRef) chips.push({ cls: 'proj', text: c.projectRef });
  if (received) chips.push({ cls: 'ch-wa', text: received.short });
  return {
    avatar: c.initials,
    avatarClass: resolved ? 'kn' : 'tri',
    name: resolved ? resolved.name : c.name,
    ref: resolved ? resolved.clientNo : null,
    identity: c.phone || null,
    chips,
    unsorted,
  };
}

/* ───────────────────────── menu content model (Context rows) ───────────────────────── */

export interface MenuRow {
  k: string;
  v: string;
  /** Optional href for a linkable value (project). */
  href?: string | null;
}

function ownerLabel(c: InboxConversation, myUid: string | null): string {
  if (c.owner === undefined || c.owner === null) return 'Unassigned';
  return myUid && c.owner.uid === myUid ? 'You' : c.owner.name;
}

/**
 * PURE — the Context rows merged into the Actions surface (replaces the old
 * always-on right Context panel). Channel · sender/contact · Status · Owner ·
 * Project · AI read — derived entirely from the row + the resolved received-on.
 */
export function contextRows(
  c: InboxConversation,
  received: ReceivedOn | null,
  resolved: LiveClient | null,
  myUid: string | null,
  /** AD-039 — the lead this conversation identity-joins to (exact match). */
  lead?: { id: string; name: string; stageLabel: string } | null,
): MenuRow[] {
  const rows: MenuRow[] = [];
  if (c.channel === 'email') {
    const em = c.email;
    rows.push({ k: 'Channel', v: received?.full || 'Email' });
    rows.push({
      k: 'Sender',
      v: em?.senderName ? `${em.senderName} · ${em.senderEmail}` : em?.senderEmail || c.name,
    });
    rows.push({
      k: 'Status',
      v: em?.knownContactId
        ? `✓ Known contact · ${em.knownContactId}`
        : '⚠ Unsorted — not a contact yet',
    });
    rows.push({ k: 'Owner', v: ownerLabel(c, myUid) });
    rows.push({
      k: 'AI read',
      v: em ? `${em.aiLabel.replace(/_/g, ' ')} · ${em.aiConfidence}` : '—',
    });
    return rows;
  }
  rows.push({ k: 'Channel', v: received?.full || 'WhatsApp' });
  rows.push({
    k: 'Contact',
    v: resolved ? `${resolved.name} · ${resolved.clientNo}` : c.phone || c.name,
  });
  rows.push({
    k: 'Status',
    v: resolved ? '✓ Known contact' : '⚠ Unsorted — not a contact yet',
  });
  // AD-039 identity join — the joined lead row (exact phone match only).
  // Rendered only when a lead joins; unlinked threads see no change here.
  if (lead) {
    rows.push({
      k: 'Lead',
      v: `${lead.name} · ${lead.stageLabel}`,
      href: `/leads/${lead.id}`,
    });
  }
  rows.push({ k: 'Owner', v: ownerLabel(c, myUid) });
  rows.push({
    k: 'Project',
    v: c.projectRef || c.projectId || 'Not linked yet',
    href: c.projectId ? `/project/${c.projectId}` : null,
  });
  rows.push({
    k: 'AI',
    v: c.aiPaused ? 'Paused — a human is replying' : 'Auto-reply ON',
  });
  return rows;
}

/** PURE — which channel lens a row belongs to. */
export function channelOf(c: InboxConversation): InboxChannel {
  return c.channel === 'email' ? 'email' : 'whatsapp';
}
